// src/hooks/useExpenseAttachments.ts - Upload et suppression des justificatifs (Firebase Storage)

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Extrait le chemin Storage à partir d'une URL de téléchargement Firebase.
 * Format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?...
 */
function getPathFromDownloadUrl(downloadUrl: string): string | null {
  try {
    const match = downloadUrl.match(/\/o\/(.+?)(\?|$)/);
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

const EXPENSE_ATTACHMENTS_PATH = 'businesses';

/**
 * Chemin Storage pour un justificatif :
 * businesses/{businessId}/expenses/{expenseId}/justificatifs/{fileName}
 * Pour une nouvelle dépense (brouillon), expenseId peut être "draft-{uuid}".
 */
function getAttachmentPath(businessId: string, expenseId: string, fileName: string): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/expenses/${expenseId}/justificatifs/${unique}`;
}

/**
 * Justificatifs notes de frais :
 * businesses/{businessId}/expenseNotes/{noteId}/justificatifs/{fileName}
 * noteId peut être "draft-{uuid}" avant création.
 */
function getExpenseNoteProofPath(businessId: string, noteId: string, fileName: string): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/expenseNotes/${noteId}/justificatifs/${unique}`;
}

/**
 * Justificatifs paiement POS (Orange Money / MTN) :
 * businesses/{businessId}/posPayments/{sessionId}/justificatifs/{fileName}
 * sessionId = draft-{uuid} avant validation, puis réutilisable avec le même id pendant la saisie du panier.
 */
function getPosPaymentProofPath(businessId: string, sessionId: string, fileName: string): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/posPayments/${sessionId}/justificatifs/${unique}`;
}

/**
 * Justificatifs paiements contrat (brouillon ou existant) :
 * businesses/{businessId}/contractPayments/{paymentIdOrSessionId}/justificatifs/{fileName}
 * paymentIdOrSessionId = "draft-{uuid}" pour nouveau paiement, ou id du doc pour paiement existant.
 */
function getContractPaymentProofPath(
  businessId: string,
  paymentIdOrSessionId: string,
  fileName: string
): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/contractPayments/${paymentIdOrSessionId}/justificatifs/${unique}`;
}

/**
 * Justificatifs versements plan de paiement :
 * businesses/{businessId}/paymentPlanPayments/{sessionId}/justificatifs/{fileName}
 */
function getPaymentPlanPaymentProofPath(
  businessId: string,
  sessionId: string,
  fileName: string
): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/paymentPlanPayments/${sessionId}/justificatifs/${unique}`;
}

/**
 * Pièces jointes des rapports d'activité (Excel / PDF / images) :
 * businesses/{businessId}/activityReports/{reportId}/attachments/{fileName}
 * reportId peut être "draft-{uuid}" avant création.
 */
function getActivityReportAttachmentPath(
  businessId: string,
  reportId: string,
  fileName: string
): string {
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const unique = `${Date.now()}_${sanitized}`;
  return `${EXPENSE_ATTACHMENTS_PATH}/${businessId}/activityReports/${reportId}/attachments/${unique}`;
}

/**
 * Upload un fichier (photo caméra ou fichier choisi) et retourne l'URL de téléchargement.
 */
export async function uploadExpenseAttachment(
  businessId: string,
  expenseId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `photo_${Date.now()}.jpg`);
  const path = getAttachmentPath(businessId, expenseId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'image/jpeg' });
  await uploadBytes(storageRef, blob);
  const url = await getDownloadURL(storageRef);
  return url;
}

export async function uploadExpenseNoteAttachment(
  businessId: string,
  noteId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `photo_${Date.now()}.jpg`);
  const path = getExpenseNoteProofPath(businessId, noteId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'image/jpeg' });
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}

/**
 * Supprime un justificatif dans Storage à partir de son URL de téléchargement.
 * (Le SDK v9+ n'exporte pas refFromURL, on extrait le chemin depuis l'URL.)
 */
export async function deleteExpenseAttachmentByUrl(url: string): Promise<void> {
  const path = getPathFromDownloadUrl(url);
  if (!path) throw new Error('URL de justificatif invalide');
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

/**
 * Upload d’un justificatif pour un split de paiement en caisse (hors espèces).
 */
export async function uploadPosPaymentProof(
  businessId: string,
  sessionId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `photo_${Date.now()}.jpg`);
  const path = getPosPaymentProofPath(businessId, sessionId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'image/jpeg' });
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}

/**
 * Upload justificatif de paiement contrat (brouillon avant création ou mise à jour).
 * paymentIdOrSessionId = "draft-{uuid}" pour RecordPaymentModal, ou paymentId pour mise à jour.
 */
export async function uploadContractPaymentProof(
  businessId: string,
  paymentIdOrSessionId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `justificatif_${Date.now()}.jpg`);
  const path = getContractPaymentProofPath(businessId, paymentIdOrSessionId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'image/jpeg' });
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}

/**
 * Upload justificatif pour un versement de plan de paiement (brouillon avant création du doc Firestore).
 */
export async function uploadPaymentPlanPaymentProof(
  businessId: string,
  sessionId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `photo_${Date.now()}.jpg`);
  const path = getPaymentPlanPaymentProofPath(businessId, sessionId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'image/jpeg' });
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}

/**
 * Upload d'une pièce jointe de rapport d'activité.
 */
export async function uploadActivityReportAttachment(
  businessId: string,
  reportId: string,
  file: File | Blob,
  fileName?: string
): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : `piece_jointe_${Date.now()}`);
  const path = getActivityReportAttachmentPath(businessId, reportId, name);
  const storageRef = ref(storage, path);
  const blob = file instanceof File ? file : new File([file], name, { type: file.type || 'application/octet-stream' });
  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
}
